import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Marquee } from './Marquee';

export const Problem = () => {
  const { t } = useTranslation();
  const symptoms = t('problem.symptoms', { returnObjects: true }) || [];
  const todoItems = t('problem.todoItems', { returnObjects: true }) || [];
  const todoCount = todoItems.length;

  return (
    <>
      {/* Lime marquee */}
      <Marquee items={t('problem.marqueeItems', { returnObjects: true }) || []} />

      <section id="problem" className="relative border-b border-ink py-20 md:py-28" data-testid="problem">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          {/* Section eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono text-xs bg-ink text-page px-2 py-1 tracking-widest2">§ 02</span>
            <span className="font-mono uppercase text-xs tracking-widest2 text-ink">{t('problem.eyebrow')}</span>
          </div>

          {/* Split heading */}
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
            <div className="col-span-12 lg:col-span-7">
              <h2 className="display text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-ink">
                {t('problem.h1')}<br />
                <span className="serif-italic text-6xl md:text-7xl lg:text-8xl">{t('problem.h1_italic')}</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:pt-6">
              <p className="serif-italic text-xl md:text-2xl leading-snug text-ink/85">
                {t('problem.lede')}
              </p>
            </div>
          </div>

          {/* Symptom cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {symptoms.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="box-line p-5 md:p-6 relative hover:-translate-y-1 transition-transform duration-500"
                data-testid={`problem-symptom-${i}`}
              >
                <div className="flex items-center gap-2 mb-4 md:mb-6">
                  <span className="text-clay text-xl leading-none">△</span>
                  <span className="font-mono uppercase text-xs tracking-widest2 text-ink">{t('problem.symptomLabel')} 0{i + 1}</span>
                </div>
                <p className="font-mono uppercase text-sm md:text-base font-medium text-ink leading-snug mb-3">
                  {s.headline}
                </p>
                <p className="serif-italic text-sm md:text-base text-ink/80 leading-snug">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Dark "To-Do Pile" card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7 }}
            className="mt-14 md:mt-20 bg-ink text-page p-6 md:p-10 relative"
            style={{ border: '1px solid #0B0B0B' }}
            data-testid="problem-todo-pile"
          >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6 md:mb-8">
              <span className="font-mono uppercase text-xs tracking-widest2 text-lime">— {t('problem.todoLabel')}</span>
              <span className="font-mono uppercase text-xs tracking-widest2 text-page/70">
                {todoCount} {t('problem.todoCount')}
              </span>
            </div>
            <h3 className="display text-5xl md:text-7xl leading-[0.9] mb-8 md:mb-10">
              {t('problem.todoTitle1')}{' '}
              <span
                className="inline-block px-2 md:px-3 text-ink"
                style={{ background: '#4CDCE6' }}
              >
                {t('problem.todoTitle2')}
              </span>{' '}
              {t('problem.todoTitle3')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {todoItems.map((item, i) => (
                <div
                  key={i}
                  className="border border-page/40 px-3 py-2.5 font-mono uppercase text-[11px] md:text-xs tracking-widest text-page flex items-start gap-2"
                >
                  <span aria-hidden className="text-lime shrink-0">▸</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};
